import React, { useMemo, useState } from "react"
import { Modal, Button } from "semantic-ui-react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose } from "@fortawesome/pro-solid-svg-icons"
import { ReactComponent as CarWarning } from "../../../images/car-warning.svg"
import { ReactComponent as Tick } from "../../../images/tick-dynamic.svg"
import { connect } from "react-redux"
import { createMembership } from "../../../v1/actions/membership"
import { routerPaths } from "../../../v1/constants/paths"
import Notifications from "../../../v1/components/Notifications"
import { withTranslation } from "react-i18next"
import { withRouter } from "react-router"
import store from "../../../shared_component/utils/configureStore"

const MembershipUpgradeModal = ({
  membershipData,
  activeMembershipPlan,
  open,
  onClose,
  createMembership,
  paymentMethods,
  t,
  user,
  history
}) => {
  const [loading, setLoading] = useState(false)
  const membership = useMemo(() => {
    if (!membershipData?.length || !activeMembershipPlan) return null
    return [...membershipData]
      .sort((a, b) => a.max_num_vehicles - b.max_num_vehicles)
      .find((plan) => plan.max_num_vehicles > activeMembershipPlan.max_num_vehicles)
  }, [membershipData, activeMembershipPlan])

  if (!membership) return null

  const isPremium = membership.name === "Premium"
  const isElite = membership.name === "Elite"

  const features = [
    isPremium && (
      <>
        Add up to <b>{membership.max_num_vehicles} vehicles</b> to your account
      </>
    ),
    isPremium && (
      <>
        Get <b>{parseInt(membership.percent_discount)}% off</b> service and repair
      </>
    ),
    isPremium && <>Talk to a Master Mechanic, any time</>,
    isElite && (
      <>
        Protect your whole garage with coverage for up to{" "}
        <b>{membership.max_num_vehicles} vehicles</b>.
      </>
    ),
    isElite && (
      <>
        Get total Peace of mind with <b>24/7 Roadside Assistance</b>
      </>
    )
  ].filter(Boolean)

  const upgradeMembership = async () => {
    const missingPayment = !paymentMethods || paymentMethods.length < 1
    const validUserData = !!user?.cellPhone && (user?.lastName?.length || 0) > 0
    if (!validUserData) {
      history.push({
        pathname: routerPaths.personalInfo,
        search: `?redirect=${
          missingPayment ? routerPaths.paymentInformation : routerPaths.selectVehicle
        }`
      })
      return
    }
    if (missingPayment) {
      history.push(routerPaths.paymentInformation)
      return
    }

    const currentMembership = membership || {}
    currentMembership.acceptedPassengerVehicleClause = true
    currentMembership.acceptedTermsAndConditions = true
    setLoading(true)
    await createMembership({
      plan: currentMembership,
      callback: (status, data) => {
        if (status === "success") {
          onClose()
          store.dispatch(
            Notifications.success({
              title: "Success",
              message: t("membership:membershipUpdateSuccessMessage"),
              position: "tr",
              autoDismiss: 5,
              action: {
                label: "Dismiss"
              }
            })
          )
        } else {
          console.log(data)
          store.dispatch(
            Notifications.error({
              title: t("errorNotificationTitle"),
              message: `${data.message || t("errorNotificationMessage")}`,
              position: "tr",
              autoDismiss: 5,
              action: {
                label: "Dismiss"
              }
            })
          )
        }
        setLoading(false)
      }
    })
  }

  return (
    <Modal open={open} className="upgrade-membership-modal">
      <Modal.Content>
        <div className="cancel-modal">
          <FontAwesomeIcon icon={faClose} className="close-icon" onClick={onClose} />

          <div className="icon-wrapper">
            <CarWarning className="warning-icon" />
          </div>

          <p className="title">
            Upgrade to {membership.name}
            <br /> for ${membership.price}/mo
          </p>

          <div className="description">
            <p>
              {isPremium
                ? `The ${activeMembershipPlan.plan_name} plan is limited to 
              ${activeMembershipPlan.max_num_vehicles} vehicles.`
                : `The ${activeMembershipPlan.plan_name} plan supports up to ${activeMembershipPlan.max_num_vehicles} vehicles.`}
            </p>
            <p>
              {isPremium
                ? `Upgrade to ${membership.name} to unlock new benefits.`
                : `For larger garages, ${membership.name} unlocks our best benefits.`}
            </p>
          </div>

          <div className="feature-list">
            {isElite && <p style={{ margin: 0, color: "black" }}>Everything in Premium, plus:</p>}
            {features.map((feature, i) => (
              <div className="feature-item" key={i}>
                <Tick />
                <p>{feature}</p>
              </div>
            ))}
          </div>

          <Button className="upgrade-btn" onClick={upgradeMembership} loading={loading}>
            Upgrade to {membership.name}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  )
}

const mapStateToProps = (state) => {
  const { paymentMethods, membershipData, user } = state

  return {
    paymentMethods: paymentMethods.braintreePaymentMethods,
    membershipData: membershipData.availableMembershipPlans,
    activeMembershipPlan: user.membership || {},
    user: user || {}
  }
}

export default withRouter(
  connect(mapStateToProps, { createMembership })(
    withTranslation(["membershipUpgradeModal", "membership"])(MembershipUpgradeModal)
  )
)
